/**
 * TimeframeDropdown Component
 *
 * A pre-configured dropdown for selecting timeframe options.
 * Loads options from BasicSettingsService and includes a "Configure..."
 * option that opens TimeframeOptionsModal for managing available options.
 * Options are grouped by category with visual section headers.
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback, useMemo } from 'react';

// 2. Internal absolute imports
import { Select, SelectItem, SelectSection, type PopoverProps } from '@enfomentis/ui';
import { Settings } from 'lucide-react';

// 3. Services & Types
import { basicSettingsService } from '@/services/BasicSettingsService';
import type { TimeframeCategory, TimeframeOption } from '@/types/schema/BasicSettings';

// 4. Utils
import {
  CATEGORY_ORDER,
  getTimeframeLabel,
  getTimeframeValue,
  sortTimeframeOptions
} from '@/utils';

// 5. Related components
import { TimeframeOptionsModal } from './TimeframeOptionsModal';

/**
 * Props for TimeframeDropdown
 */
export interface TimeframeDropdownProps {
  /** Selected canonical value (e.g., "15m", "4H") or null */
  value: string | null;
  /** Callback when selection changes */
  onChange: (value: string) => void;
  /** Optional label for the Select */
  label?: string;
  /** Optional placeholder text */
  placeholder?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Disabled state */
  isDisabled?: boolean;
  /** Optional className for container */
  className?: string;
  /** Disable animation (fixes aria-hidden warning when used inside Modal) */
  disableAnimation?: boolean;
  /** Popover props to pass through to Select (use portalContainer for modal contexts) */
  popoverProps?: Partial<PopoverProps>;
}

/**
 * Special key for the "Configure..." option
 */
const CONFIGURE_KEY = '__configure__';

/**
 * Display names for each category (used as section titles)
 */
const CATEGORY_DISPLAY_NAMES: Record<TimeframeCategory, string> = {
  ticks: 'Ticks',
  minutes: 'Minutes',
  hours: 'Hours',
  days: 'Days',
  weeks: 'Weeks',
  months: 'Months',
  range: 'Range'
};

/**
 * TimeframeDropdown Component
 */
export const TimeframeDropdown: React.FC<TimeframeDropdownProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Select timeframe',
  size = 'md',
  isDisabled = false,
  className,
  disableAnimation = false,
  popoverProps,
}) => {
  // Options state
  const [options, setOptions] = useState<TimeframeOption[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Load options from settings
   */
  const loadOptions = useCallback(async () => {
    try {
      setLoading(true);
      const settings = await basicSettingsService.getOrCreateDefaultSettings();
      setOptions(settings.timeframeOptions);
    } catch (err) {
      console.error('Failed to load timeframe options:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load options on mount
  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  /**
   * Handle selection change
   */
  const handleSelectionChange = (keys: 'all' | Set<React.Key>) => {
    if (keys === 'all') return;

    const selected = Array.from(keys)[0] as string | undefined;
    if (!selected) return;

    if (selected === CONFIGURE_KEY) {
      // Open configuration modal
      setIsModalOpen(true);
    } else {
      // Normal selection - pass canonical value to onChange
      onChange(selected);
    }
  };

  /**
   * Handle modal close - refresh options
   */
  const handleModalClose = () => {
    setIsModalOpen(false);
    loadOptions();
  };

  /**
   * Handle options change from modal
   */
  const handleOptionsChange = (newOptions: TimeframeOption[]) => {
    setOptions(newOptions);
  };

  // Sort options for display (service already sorts, but defensive)
  const sortedOptions = useMemo(() => sortTimeframeOptions(options), [options]);

  // Group options by category
  const groupedOptions = useMemo(() => {
    const grouped = new Map<TimeframeCategory, TimeframeOption[]>();
    for (const option of sortedOptions) {
      const existing = grouped.get(option.category) || [];
      grouped.set(option.category, [...existing, option]);
    }
    return grouped;
  }, [sortedOptions]);

  // Get populated categories in order
  const populatedCategories = useMemo(() => {
    return CATEGORY_ORDER.filter((cat) => (groupedOptions.get(cat)?.length ?? 0) > 0);
  }, [groupedOptions]);

  // Determine selected keys (gate on loading and value existence to avoid HeroUI warnings)
  const hasValue = value && sortedOptions.some(
    (opt) => getTimeframeValue(opt.category, opt.interval) === value
  );
  const selectedKeys = loading || !hasValue ? [] : [value];

  // Check if we have any options
  const hasOptions = sortedOptions.length > 0;

  return (
    <>
      <Select
        label={label}
        placeholder={placeholder}
        selectedKeys={selectedKeys}
        onSelectionChange={handleSelectionChange}
        size={size}
        isDisabled={isDisabled || loading}
        disableAnimation={disableAnimation}
        {...(popoverProps && { popoverProps })}
        {...(className && { className })}
        aria-label={label || 'Timeframe selection'}
      >
        {!hasOptions && !loading
          ? // Empty state - no sections, just items (ObjectiveModal pattern)
            [
              <SelectItem key="__no_options__" isDisabled>
                No timeframes configured
              </SelectItem>,
              <SelectItem
                key={CONFIGURE_KEY}
                startContent={<Settings size={16} />}
                className="text-primary"
              >
                Configure...
              </SelectItem>
            ]
          : // Populated state - grouped sections
            [
              // Category sections (all have showDivider to separate from next section)
              ...populatedCategories.map((category) => {
                const categoryOptions = groupedOptions.get(category) || [];

                return (
                  <SelectSection
                    key={category}
                    title={CATEGORY_DISPLAY_NAMES[category]}
                    showDivider
                  >
                    {categoryOptions.map((option) => (
                      <SelectItem
                        key={getTimeframeValue(option.category, option.interval)}
                      >
                        {getTimeframeLabel(option.category, option.interval)}
                      </SelectItem>
                    ))}
                  </SelectSection>
                );
              }),
              // Configure section (no divider - last section)
              <SelectSection key="__configure_section__">
                <SelectItem
                  key={CONFIGURE_KEY}
                  startContent={<Settings size={16} />}
                  className="text-primary"
                >
                  Configure...
                </SelectItem>
              </SelectSection>
            ]}
      </Select>

      <TimeframeOptionsModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onOptionsChange={handleOptionsChange}
      />
    </>
  );
};

export default TimeframeDropdown;
